precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uBass;
uniform float uMid;
uniform float uHigh;
uniform float uAudioAverage;
uniform float uZoom;
uniform vec2 uOffset;
uniform vec3 uColorPrimary;
uniform vec3 uColorSecondary;
uniform vec3 uColorAccent;
uniform vec2 uJuliaC;
uniform vec2 uDeviceTilt;

varying vec2 vUv;

const int MAX_ITERATIONS = 200;

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / min(uResolution.x, uResolution.y);
  
  // Scale and offset with device tilt
  vec2 tiltOffset = uDeviceTilt * (1.0 / uZoom);
  vec2 z = uv * 2.5 / uZoom + uOffset + tiltOffset;
  
  // Julia set constant - morphs over time
  vec2 c = uJuliaC;
  
  float iterations = 0.0;
  float smooth_iter = 0.0;
  
  for (int i = 0; i < MAX_ITERATIONS; i++) {
    if (dot(z, z) > 256.0) break;
    
    // z = z^2 + c
    float xTemp = z.x * z.x - z.y * z.y + c.x;
    z.y = 2.0 * z.x * z.y + c.y;
    z.x = xTemp;
    
    iterations += 1.0;
  }
  
  // Smooth iteration count
  if (iterations < float(MAX_ITERATIONS)) {
    float log_zn = log(dot(z, z)) / 2.0;
    float nu = log(log_zn / log(2.0)) / log(2.0);
    smooth_iter = iterations + 1.0 - nu;
  } else {
    smooth_iter = iterations;
  }
  
  float t = smooth_iter / float(MAX_ITERATIONS);
  
  vec3 col;
  
  if (iterations >= float(MAX_ITERATIONS)) {
    // Inside the set
    col = uColorPrimary * 0.2;
    // Add subtle inner glow
    col += uColorSecondary * uBass * 0.3;
  } else {
    // Create flowing color bands
    float angle = atan(z.y, z.x);
    float radius = length(z);
    
    float hue = t * 2.0 + angle * 0.1 + uTime * 0.02;
    
    // Library of versions aesthetic - multiple overlapping patterns
    vec3 layer1 = uColorSecondary * (0.5 + 0.5 * sin(hue * 6.28318));
    vec3 layer2 = uColorAccent * (0.5 + 0.5 * cos(hue * 6.28318 + 2.094));
    vec3 layer3 = uColorPrimary * (0.5 + 0.5 * sin(hue * 6.28318 + 4.188));
    
    // Blend layers based on iteration depth
    float blend1 = smoothstep(0.0, 0.4, t);
    float blend2 = smoothstep(0.3, 0.7, t);
    
    col = mix(layer1, layer2, blend1);
    col = mix(col, layer3, blend2);
    
    // Audio reactivity - stronger shimmer and pulse
    col += uColorAccent * uMid * 0.5 * sin(smooth_iter * 0.5 + uTime);
    col += uColorSecondary * uBass * 0.3;
    col *= 1.0 + uHigh * 0.25;
    
    // Add subtle radial glow
    col *= 1.0 - t * 0.3;
  }
  
  // Subtle film grain
  float grain = fract(sin(dot(uv + uTime * 0.01, vec2(12.9898, 78.233))) * 43758.5453);
  col += (grain - 0.5) * 0.02;
  
  // Vignette
  vec2 vigUv = vUv * 2.0 - 1.0;
  float vignette = 1.0 - dot(vigUv, vigUv) * 0.25;
  col *= vignette;
  
  gl_FragColor = vec4(col, 1.0);
}

